import React from "react";

const article = {
  slug: "choosing-a-control-panel-builder",
  title: "Choosing a control panel builder: what to look for and what to avoid",
  dek: "A well-built control panel is invisible for a decade. A badly-built one is a headache the day it's energised. Here's how to evaluate a UK panel builder before you place the order — and the questions worth asking every quote.",
  category: "Electrical",
  readTime: "8 min read",
  publishedAt: "2026-02-05",
  image: "/gallery/db-panel-3.jpg",
  imagePosition: "center 40%",
  relatedServiceSlugs: [
    "control-panel-design-and-build",
    "industrial-electrical-installation",
    "plc-programming",
    "eicr-and-fixed-wire-testing",
  ],
  faqs: [
    {
      q: "Which standard should a UK control panel be built to?",
      a: "The relevant standard for low-voltage switchgear and controlgear assemblies is BS EN 61439 (parts 1 and 2 for general-purpose and power-switchgear assemblies). A competent panel builder will design, verify and mark the panel to that standard — including form of separation, temperature rise, short-circuit withstand and IP rating declarations. Ask to see the verification method (design or testing) and how it applies to your build.",
    },
    {
      q: "Should the panel be tested before delivery?",
      a: "Yes — a Factory Acceptance Test (FAT) at the builder's workshop is normal practice. It should include continuity, insulation resistance, functional test of every input and output, HMI walkthrough, and — where applicable — soft simulation of the PLC program. FAT gives you a chance to sign off the panel before it ships, and to find issues while parts and tooling are still to hand.",
    },
    {
      q: "What paperwork should come with the panel?",
      a: "As a minimum: an as-built wiring diagram set (not the tender drawing set), a bill of materials with device references and firmware versions, the BS EN 61439 verification statement, test certificates, an operating and maintenance (O&M) manual, and — for panels with software — a controlled program backup with the version and date. Anything less is not a finished product.",
    },
    {
      q: "How do I know if a panel builder is any good before I place the order?",
      a: "Ask to visit the workshop. Look at panels currently in build, panels awaiting shipment and panels being tested. Neat, laced and labelled looms; consistent wire numbering; clean earthing; sensible ventilation; component brands you recognise. Ask about their approach to BS EN 61439 verification. If the tour is offered without hesitation and the workshop matches the marketing photos, you're on the right track.",
    },
  ],
  body: (
    <>
      <p>
        Every project needing a control panel has a version of the same
        conversation: cheapest quote, cheapest lead time, or the one from the
        builder somebody vaguely remembers being &quot;good&quot;. It&apos;s
        remarkable how often the panel then becomes the most-blamed item on
        the plant for the following five years — even when it wasn&apos;t
        actually the root cause.
      </p>
      <p>
        A well-built control panel is a boring piece of kit. It sits in the
        electrical room, does its job, and asks for nothing but the
        occasional wipe of the filter. A badly-built one throws up trip
        events, loose terminations, unlabelled cores and support calls from
        day one. The difference isn&apos;t magic; it&apos;s discipline in the
        specification, the workshop and the paperwork.
      </p>

      <h2>The standard your panel should be built to</h2>
      <p>
        In the UK and EU, the relevant standard for low-voltage switchgear
        and controlgear assemblies is <strong>BS EN 61439</strong>. Part 1 sets
        out the general rules; Part 2 covers power switchgear and controlgear
        assemblies (the industrial cabinets most factories care about). The
        standard requires the panel builder to declare and verify a range of
        design characteristics:
      </p>
      <ul>
        <li>Rated voltage, current and frequency</li>
        <li>Short-circuit withstand strength</li>
        <li>Temperature rise inside the assembly</li>
        <li>Ingress protection rating (IP)</li>
        <li>Form of separation (typically Form 2, 3 or 4 for industrial builds)</li>
        <li>Protection against electric shock and functional characteristics</li>
      </ul>
      <p>
        Verification can be by design (calculation and reference to a tested
        assembly) or by test. Either is acceptable — but the builder must be
        able to tell you which method they&apos;ve used, and the
        documentation must reflect it. A quote that ignores BS EN 61439 is a
        quote from a builder who hasn&apos;t thought about the panel as an
        engineered product.
      </p>

      <h2>Workshop signals that a builder is competent</h2>
      <p>
        The tender documents will tell you very little. The workshop tells
        you almost everything. If you can, arrange a visit before you place
        the order — most reputable builders offer one without prompting.
        Things worth looking at:
      </p>
      <ul>
        <li>
          <strong>Wire dressing.</strong> Cores laced and combed to a
          consistent standard. Terminals landed one-per-core (not two). Ferrules
          and heat-shrink numbering, not felt-tip.
        </li>
        <li>
          <strong>Component brands.</strong> Consistent, recognisable
          manufacturers. Mixing brands isn&apos;t automatically wrong — but
          random components from unknown suppliers usually signal a
          price-first buying policy.
        </li>
        <li>
          <strong>Earthing.</strong> A clean earth bar, star-point where
          appropriate, tagged bonding for panel doors and gland plates.
        </li>
        <li>
          <strong>Layout logic.</strong> Power components separated from
          control; DC and AC segregated; sensible ventilation paths; a
          maintenance-friendly layout so the electrician who works on it in
          three years&apos; time isn&apos;t reaching over live copper.
        </li>
        <li>
          <strong>Labels.</strong> Engraved traffolyte or laser-printed
          adhesive labels — not hand-written tape. Consistent across the door
          and inside the enclosure.
        </li>
      </ul>

      <h2>Design decisions worth pushing back on</h2>
      <p>
        Some decisions get made lazily and cost you for the life of the
        panel. When you review the design, insist on:
      </p>
      <ul>
        <li>
          <strong>Sensible spare capacity.</strong> Extra terminals on the DIN
          rail, spare I/O on the PLC, and a couple of RCBOs left as
          &quot;spare&quot; on distribution. Loading the panel to 100% at
          build leaves no headroom for the modifications every plant
          eventually needs.
        </li>
        <li>
          <strong>The right form of separation.</strong> Form 2 is common but
          not always sufficient — for panels where different personnel need
          to work on different sections while others remain live, Form 3 or
          Form 4 pays for itself.
        </li>
        <li>
          <strong>Realistic thermal design.</strong> Ambient in the switch
          room, projected load factor, VSD losses. A panel that meets its
          temperature rise on the bench in Wolverhampton in November may
          not meet it in a factory boiler house in July.
        </li>
        <li>
          <strong>Segregation of safety circuits.</strong> Safety relays,
          e-stop wiring and interlock circuits kept clearly separated,
          coloured and documented per BS EN ISO 13849 or IEC 62061 as
          applicable to the machine.
        </li>
      </ul>

      <h2>Factory Acceptance Test — the sign-off you always want</h2>
      <p>
        A Factory Acceptance Test (FAT) at the builder&apos;s workshop lets
        you verify the panel does what the spec says, before it&apos;s
        boxed up. A workable FAT covers:
      </p>
      <ol>
        <li>Visual and mechanical inspection against the drawings</li>
        <li>Continuity and insulation resistance test on all circuits</li>
        <li>Functional test of every input, every output, every interlock</li>
        <li>HMI walkthrough — every screen, every alarm, every language</li>
        <li>PLC program comparison against the master copy</li>
        <li>Simulated fault injection — RCD, MCB, e-stop, thermal trip</li>
        <li>Documented punch list of any snags found and how they&apos;ll be closed</li>
      </ol>
      <p>
        Sign off the FAT in writing. Any snags close out before dispatch, not
        after. Fixing a wiring error on the workshop bench takes minutes;
        fixing it inside a live plant costs a shift.
      </p>

      <h2>The paperwork you must receive</h2>
      <p>
        A finished panel is a physical object <em>and</em> a document set. If
        the paperwork is thin, the panel isn&apos;t finished. As a minimum,
        expect:
      </p>
      <ul>
        <li>As-built wiring diagrams — not the tender set</li>
        <li>Bill of materials with device references and firmware versions</li>
        <li>BS EN 61439 verification statement and rating plate</li>
        <li>Test certificates from FAT</li>
        <li>PLC / HMI program backup with version, date and change log</li>
        <li>O&amp;M manual with recommended maintenance intervals and spare parts list</li>
        <li>Datasheets for every non-standard component</li>
      </ul>
      <p>
        A builder who is confident in their work supplies this without being
        chased. A builder who is uncomfortable about handing it over is a
        builder to walk away from.
      </p>

      <h2>Red flags to walk away from</h2>
      <ul>
        <li>No BS EN 61439 verification statement, or vague answers when you ask</li>
        <li>No workshop visit offered — or one hastily arranged</li>
        <li>No FAT included in the price</li>
        <li>Program source code held hostage &quot;for support&quot;</li>
        <li>Photos of the workshop that look nothing like the panels you&apos;re shown</li>
        <li>Sub-contracted build with no traceable QA sign-off</li>
      </ul>

      <h2>The bottom line</h2>
      <p>
        The best time to find a panel builder is well before you actually
        need one. Visit workshops, ask for BS EN 61439 evidence, and place a
        small pilot job with a builder before you trust them with a
        production-critical build. The panels that last a decade are the ones
        specified, built and signed off by people who care about them being
        boring — because boring, in a control panel, is the highest
        compliment there is.
      </p>

      <h2>A note on aftercare and support</h2>
      <p>
        The relationship with a panel builder doesn&apos;t end at delivery.
        The best builders quietly become the people you call when a
        controller drops out at 2am, when you need a spare drive next
        morning, or when the specification changes six months later and the
        panel needs modifying. When you evaluate quotes, weigh the answers
        to the questions below at least as heavily as the price line:
      </p>
      <ul>
        <li>
          <strong>Spares strategy.</strong> Are recommended spare parts
          identified in the O&amp;M pack, with realistic lead times? Is there
          a stocked-holdings offer for critical items?
        </li>
        <li>
          <strong>Software support.</strong> Do you keep a controlled copy of
          the source? Is the code laid out and commented well enough that a
          third-party controls engineer could pick it up if you ever needed
          them to?
        </li>
        <li>
          <strong>Modification workflow.</strong> When we want to change the
          panel in the future, what&apos;s the process — quote to updated
          drawings to updated software backup? Who owns each step?
        </li>
        <li>
          <strong>Response for emergencies.</strong> If a component fails at
          06:00, what does &quot;support&quot; look like in practice? A
          number that rings, an engineer who answers, and a realistic
          expectation of when a replacement is on site.
        </li>
      </ul>
      <p>
        Answers to these questions tell you whether you are dealing with a
        supplier who ships boxes and disappears, or a partner who expects
        to hear from you occasionally for the next decade. The second
        category is worth paying a little more for.
      </p>
    </>
  ),
};

export default article;
